import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Globe, Mail, Lock, KeyRound, ArrowLeft } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { authAPI } from '../services/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'done'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      toast({ title: 'Code Sent', description: 'Check your email for the reset code.' });
      setStep('code');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to send reset code', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(email, code, newPassword);
      toast({ title: 'Password Reset', description: 'Your password has been reset. Please login.' });
      setStep('done');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to reset password', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-sky-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Globe className="h-10 w-10 text-amber-600" />
            <span className="text-2xl font-bold text-slate-900">NomadLinguist</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Reset Password</h1>
          <p className="text-slate-600">We'll help you get back into your account</p>
        </div>

        <Card className="border-2">
          <CardHeader>
            <CardTitle>
              {step === 'email' && 'Enter Your Email'}
              {step === 'code' && 'Enter Reset Code'}
              {step === 'done' && 'Password Reset!'}
            </CardTitle>
            <CardDescription>
              {step === 'email' && 'We\'ll generate a reset code for your account'}
              {step === 'code' && 'Enter the code and your new password'}
              {step === 'done' && 'You can now login with your new password'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'email' && (
              <form onSubmit={handleRequestCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? 'Sending...' : 'Get Reset Code'}
                </Button>
              </form>
            )}

            {step === 'code' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-center">
                  <p className="text-sm text-sky-700">A 6-digit code has been sent to <strong>{email}</strong></p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Reset Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="code"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      className="pl-10"
                      maxLength={6}
                      required
                      data-testid="reset-code-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="New password (min 6 chars)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="reset-new-password"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="reset-confirm-password"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" disabled={loading} data-testid="reset-submit-btn">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}

            {step === 'done' && (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="h-8 w-8 text-emerald-600" />
                </div>
                <Button className="w-full bg-amber-600 hover:bg-amber-700" onClick={() => navigate('/login')} data-testid="goto-login-btn">
                  Go to Login
                </Button>
              </div>
            )}

            <div className="mt-6 text-center">
              <button onClick={() => navigate('/login')} className="text-sm text-amber-600 hover:text-amber-700 flex items-center justify-center gap-1 mx-auto">
                <ArrowLeft className="h-3 w-3" /> Back to Login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
